/**
 * lp-015. Highlighting the words a finding is about, without touching the DOM.
 *
 * The obvious way to highlight a phrase inside a paragraph is to wrap it in a
 * span. That is not available here: the lease is `contenteditable` and its
 * content is what the drafter is editing, so an element we inject can be split
 * by a keystroke, merged by a paste, dragged somewhere else, or serialised into
 * something that leaves the building. The document belongs to the customer;
 * annotations do not go in it.
 *
 * The CSS Custom Highlight API paints ranges instead. A `Range` is a pair of
 * (text node, offset) positions held outside the tree, `::highlight()` styles
 * them, and the DOM is never modified — nothing to serialise, nothing to split,
 * and no absolutely-positioned overlay to drift when the text reflows.
 *
 * Where it is unavailable the panel falls back to marking whole paragraphs with
 * an attribute, which is what it did before this existed.
 */

// One name, not one per severity. Only ever one finding is selected, and the
// panel has already said how serious it is — the highlight's job is "here",
// and colouring it by severity only competes with the marks the document
// already carries in its own yellow.
const REGISTRY_NAME = 'lp-check';

// The flash is a second registered name holding the same ranges. Highlight
// pseudo-elements are not animatable, so there is nothing to transition — but
// moving the ranges between two names repaints instantly, which is the whole
// effect, and still puts nothing in the document.
const FLASH_NAME = 'lp-check-flash';

// Every finding at once, quietly, in its own registry so the selected one can
// still be painted over the top without either erasing the other.
//
// This is what replaced marking whole blocks. A finding is about words, and an
// outline round the paragraph containing them says only "somewhere in here" —
// on a six-line clause with one bad figure in it, that is most of a paragraph
// of misdirection. These ranges start and end exactly where the text does.
const AMBIENT_NAME = 'lp-check-all';

// How long each pulse lasts. Two pulses at this rate is about 2.4 flashes a
// second, comfortably under the three-per-second ceiling that makes flashing
// content a hazard rather than a hint.
const PULSE_ON = 220;
const PULSE_OFF = 200;

let painted = null;
let ambient = null;
// Which finding each ambient range belongs to, and the hits it was built from.
//
// The ranges are the thing that goes stale: a `Range` holds a text node and an
// offset, and the editor splits text nodes whenever the caret lands somewhere
// it wants to inspect the decorations. The HITS do not go stale — they are
// `{pid, text}` — so keeping them is what makes a repaint possible without the
// caller having to know a repaint happened.
let ambientRanges = [];
let ambientHits = [];
let ambientRoot = null;
let timers = [];

function prefersStillness() {
  return Boolean(
    typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  );
}

function stopFlashing() {
  timers.forEach(clearTimeout);
  timers = [];
  if (!supportsPreciseHighlights()) return;
  CSS.highlights.delete(FLASH_NAME);
  if (painted) CSS.highlights.set(REGISTRY_NAME, painted);
}

/**
 * A paragraph's text as the outline read it.
 *
 * The projection normalises the spaces that Word leaves behind; the live DOM
 * still has them. Replacing rather than stripping keeps every offset aligned,
 * so a phrase separated by a non-breaking space is still found.
 */
function comparable(text) {
  return String(text).replace(/[\u00a0\u2007\u202f]/g, ' ');
}

export function supportsPreciseHighlights() {
  return (
    typeof CSS !== 'undefined' &&
    typeof Highlight === 'function' &&
    !!CSS.highlights
  );
}

/**
 * The text nodes of an element, in reading order, skipping the branches of the
 * form that are switched off — the same text the outline read, so an offset
 * counted there means the same thing here.
 */
export function textNodesIn(element) {
  const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      if (!node.nodeValue) return NodeFilter.FILTER_REJECT;
      const parent = node.parentElement;
      if (parent && parent.closest('.lp-hide-element'))
        return NodeFilter.FILTER_REJECT;
      return NodeFilter.FILTER_ACCEPT;
    },
  });

  const nodes = [];
  let node = walker.nextNode();
  while (node) {
    nodes.push(node);
    node = walker.nextNode();
  }
  return nodes;
}

/**
 * A range over the `occurrence`-th appearance of `needle` inside `element`.
 *
 * Found by searching the live text rather than by trusting an offset recorded
 * against the projection: the two are built from the same nodes, but a phrase
 * that has since been edited should quietly fail to match instead of
 * confidently highlighting whatever now sits at that offset.
 */
export function rangeFor(element, needle, occurrence = 0) {
  if (!element || !needle) return null;

  const nodes = textNodesIn(element);
  if (!nodes.length) return null;

  const whole = comparable(nodes.map(node => node.nodeValue).join(''));
  const wanted = comparable(needle);

  // A run of underscores contains every shorter run of underscores. Searching
  // for "______" inside "__________" matches at the first character and lands
  // the highlight on the wrong blank — and then consumes the occurrence, so the
  // right one is never reached. Hence: a match only counts if it is not part of
  // something longer made of the same character.
  const first = wanted[0];
  const last = wanted[wanted.length - 1];
  const isWholeMatch = at =>
    whole[at - 1] !== first && whole[at + wanted.length] !== last;

  let start = -1;
  let found = 0;
  for (;;) {
    start = whole.indexOf(wanted, start + 1);
    if (start === -1) return null;
    if (!isWholeMatch(start)) continue;
    if (found === occurrence) break;
    found += 1;
  }
  const end = start + wanted.length;

  const position = offset => {
    let seen = 0;
    for (let i = 0; i < nodes.length; i += 1) {
      const length = nodes[i].nodeValue.length;
      if (offset <= seen + length) return { node: nodes[i], offset: offset - seen };
      seen += length;
    }
    return null;
  };

  const from = position(start);
  const to = position(end);
  if (!from || !to) return null;

  const range = document.createRange();
  range.setStart(from.node, from.offset);
  range.setEnd(to.node, to.offset);
  return range;
}

/**
 * Paint every place a finding points at.
 *
 * `hits` are `{ pid, text }`; several can share a pid, and the same phrase can
 * appear in one paragraph more than once — "a blank in 152 places" is one
 * finding covering the whole document — so occurrences are counted per pid.
 */
export function paintHighlights(root, hits) {
  if (!supportsPreciseHighlights()) return 0;

  clearHighlights();
  if (!root || !hits || !hits.length) return 0;

  const seen = new Map();
  const ranges = [];

  hits.forEach(hit => {
    if (!hit || !hit.pid || !hit.text) return;

    const key = `${hit.pid}::${hit.text}`;
    const occurrence = seen.get(key) || 0;
    seen.set(key, occurrence + 1);

    const block = root.querySelector(`[pid="${hit.pid}"]`);
    if (!block) return;

    const range = rangeFor(block, hit.text, occurrence);
    if (range) ranges.push(range);
  });

  if (!ranges.length) return 0;

  painted = new Highlight(...ranges);
  CSS.highlights.set(REGISTRY_NAME, painted);
  return ranges.length;
}

/**
 * Paint every finding's words, faintly.
 *
 * Same range-finding as `paintHighlights`, into a different registry: the two
 * layers coexist, so selecting a finding adds emphasis rather than replacing
 * the map. Returns how many ranges landed, which is also how the caller knows
 * whether the browser could do this at all.
 */
export function paintAmbient(root, hits) {
  if (!supportsPreciseHighlights()) return 0;

  clearAmbient();
  if (!root || !hits || !hits.length) return 0;

  // Held so `repaintAmbient` can rebuild from them later without the caller
  // knowing anything went stale. Cleared by `clearAmbient` for the same reason:
  // holding them past a clear is what let a repaint bring back marks somebody
  // had already dealt with.
  ambientHits = hits;
  ambientRoot = root;

  const seen = new Map();
  const ranges = [];

  hits.forEach(hit => {
    if (!hit || !hit.pid || !hit.text) return;

    const key = `${hit.pid}::${hit.text}`;
    const occurrence = seen.get(key) || 0;
    seen.set(key, occurrence + 1);

    const block = root.querySelector(`[pid="${hit.pid}"]`);
    if (!block) return;

    const range = rangeFor(block, hit.text, occurrence);
    if (!range) return;

    ranges.push(range);
    ambientRanges.push({ findingId: hit.findingId || null, range: range });
  });

  if (!ranges.length) return 0;

  ambient = new Highlight(...ranges);
  CSS.highlights.set(AMBIENT_NAME, ambient);
  return ranges.length;
}

/**
 * Build the ranges again from the hits they came from.
 *
 * A `Range` holds a text node and an offset into it. The editor splits text
 * nodes constantly — placing the caret makes it inspect what decorations apply
 * there, and inspecting means splitting — so a range painted a moment ago can
 * be pointing at a node that is no longer part of the document, or at half of
 * one. The highlight then disappears, or survives in pieces, which is exactly
 * what it looked like.
 *
 * Nothing here is a workaround for that: these ranges were always DERIVED from
 * `{pid, text, occurrence}`, and re-deriving them is the cheap and correct
 * answer. What was missing was anything to notice the document had moved.
 */
export function repaintAmbient() {
  if (!ambientHits.length || !ambientRoot) return 0;
  return paintAmbient(ambientRoot, ambientHits);
}

/**
 * The finding whose words contain this point, if any.
 *
 * For clicking a red underline and being taken to what it says. The ranges are
 * not in the document, so there is no element to hang a listener on — the point
 * has to be tested against the ranges themselves.
 */
export function findingAtPoint(node, offset) {
  if (!node) return null;

  const found = ambientRanges.find(entry => {
    try {
      return entry.range.isPointInRange(node, offset);
    } catch (error) {
      // A range whose nodes have been detached by an edit. It is about to be
      // rebuilt; it just cannot answer this question first.
      return false;
    }
  });

  return found ? found.findingId : null;
}

export function clearAmbient() {
  ambient = null;
  ambientRanges = [];
  // The hits go too. Without this, `clearAmbient()` hid the marks and the very
  // next mutation repainted them from the hits it had kept: a drafter who fixed
  // the last finding saw the underlines come back on correct text, and clicking
  // one opened a finding that no longer existed.
  ambientHits = [];
  ambientRoot = null;
  if (!supportsPreciseHighlights()) return;
  CSS.highlights.delete(AMBIENT_NAME);
}

export function clearHighlights() {
  timers.forEach(clearTimeout);
  timers = [];
  painted = null;
  if (!supportsPreciseHighlights()) return;
  CSS.highlights.delete(REGISTRY_NAME);
  CSS.highlights.delete(FLASH_NAME);
}

/**
 * Pulse whatever is currently highlighted.
 *
 * For "take me back there": the scroll lands you in the right place, but on a
 * page of dense type a wash you were already looking at a minute ago does not
 * necessarily catch the eye again. Two pulses do.
 *
 * Skipped entirely when the reader has asked for reduced motion — the
 * highlight is already visible, and the flash is emphasis rather than
 * information, so there is nothing lost by not showing it.
 */
export function flashHighlights(pulses = 2) {
  if (!supportsPreciseHighlights() || !painted) return false;

  stopFlashing();
  if (prefersStillness()) return false;

  let at = 0;
  for (let i = 0; i < pulses; i += 1) {
    timers.push(
      setTimeout(() => {
        CSS.highlights.delete(REGISTRY_NAME);
        CSS.highlights.set(FLASH_NAME, painted);
      }, at),
    );
    at += PULSE_ON;

    timers.push(
      setTimeout(() => {
        CSS.highlights.delete(FLASH_NAME);
        CSS.highlights.set(REGISTRY_NAME, painted);
      }, at),
    );
    at += PULSE_OFF;
  }

  return true;
}
